import React from 'react';

export interface FmlLifeCycleContextInterface {
  onFmlViewWillEnter: (callback: () => void) => void;
  fmlViewWillEnter: () => void;
  onFmlViewDidEnter: (callback: () => void) => void;
  fmlViewDidEnter: () => void;
  onFmlViewWillLeave: (callback: () => void) => void;
  fmlViewWillLeave: () => void;
  onFmlViewDidLeave: (callback: () => void) => void;
  fmlViewDidLeave: () => void;
  cleanupFmlViewWillEnter: (callback: () => void) => void;
  cleanupFmlViewDidEnter: (callback: () => void) => void;
  cleanupFmlViewWillLeave: (callback: () => void) => void;
  cleanupFmlViewDidLeave: (callback: () => void) => void;
}

export const FmlLifeCycleContext = /*@__PURE__*/ React.createContext<FmlLifeCycleContextInterface>({
  onFmlViewWillEnter: () => {
    return;
  },
  fmlViewWillEnter: () => {
    return;
  },
  onFmlViewDidEnter: () => {
    return;
  },
  fmlViewDidEnter: () => {
    return;
  },
  onFmlViewWillLeave: () => {
    return;
  },
  fmlViewWillLeave: () => {
    return;
  },
  onFmlViewDidLeave: () => {
    return;
  },
  fmlViewDidLeave: () => {
    return;
  },
  cleanupFmlViewWillEnter: () => {
    return;
  },
  cleanupFmlViewDidEnter: () => {
    return;
  },
  cleanupFmlViewWillLeave: () => {
    return;
  },
  cleanupFmlViewDidLeave: () => {
    return;
  },
});

export interface LifeCycleCallback {
  (): void | (() => void | undefined);
  id?: number;
}

export interface LifeCycleDestructor {
  id: number;
  destructor: ReturnType<LifeCycleCallback>;
}

export const DefaultFmlLifeCycleContext = class implements FmlLifeCycleContextInterface {
  fmlViewWillEnterCallbacks: LifeCycleCallback[] = [];
  fmlViewDidEnterCallbacks: LifeCycleCallback[] = [];
  fmlViewWillLeaveCallbacks: LifeCycleCallback[] = [];
  fmlViewDidLeaveCallbacks: LifeCycleCallback[] = [];
  componentCanBeDestroyedCallback?: () => void;
  fmlViewWillEnterDestructorCallbacks: LifeCycleDestructor[] = [];
  fmlViewDidEnterDestructorCallbacks: LifeCycleDestructor[] = [];
  fmlViewWillLeaveDestructorCallbacks: LifeCycleDestructor[] = [];
  fmlViewDidLeaveDestructorCallbacks: LifeCycleDestructor[] = [];

  onFmlViewWillEnter(callback: LifeCycleCallback) {
    if (callback.id) {
      const index = this.fmlViewWillEnterCallbacks.findIndex((x) => x.id === callback.id);
      if (index > -1) {
        this.fmlViewWillEnterCallbacks[index] = callback;
      } else {
        this.fmlViewWillEnterCallbacks.push(callback);
      }
    } else {
      this.fmlViewWillEnterCallbacks.push(callback);
    }
  }

  teardownCallback(callback: LifeCycleCallback, callbacks: any[]) {
    // Find any destructors that have been registered for the callback
    const matches = callbacks.filter((x) => x.id === callback.id);
    if (matches.length !== 0) {
      // Execute the destructor for each matching item
      matches.forEach((match) => {
        if (match && typeof match.destructor === 'function') {
          match.destructor();
        }
      });
      // Remove all matching items from the array
      callbacks = callbacks.filter((x) => x.id !== callback.id);
    }
  }

  /**
   * Tears down the user-provided fmlViewWillEnter lifecycle callback.
   * This is the same behavior as React's useEffect hook. The callback
   * is invoked when the component is unmounted.
   */
  cleanupFmlViewWillEnter(callback: LifeCycleCallback) {
    this.teardownCallback(callback, this.fmlViewWillEnterDestructorCallbacks);
  }

  /**
   * Tears down the user-provided fmlViewDidEnter lifecycle callback.
   * This is the same behavior as React's useEffect hook. The callback
   * is invoked when the component is unmounted.
   */
  cleanupFmlViewDidEnter(callback: LifeCycleCallback) {
    this.teardownCallback(callback, this.fmlViewDidEnterDestructorCallbacks);
  }

  /**
   * Tears down the user-provided fmlViewWillLeave lifecycle callback.
   * This is the same behavior as React's useEffect hook. The callback
   * is invoked when the component is unmounted.
   */
  cleanupFmlViewWillLeave(callback: LifeCycleCallback) {
    this.teardownCallback(callback, this.fmlViewWillLeaveDestructorCallbacks);
  }

  /**
   * Tears down the user-provided fmlViewDidLeave lifecycle callback.
   * This is the same behavior as React's useEffect hook. The callback
   * is invoked when the component is unmounted.
   */
  cleanupFmlViewDidLeave(callback: LifeCycleCallback) {
    this.teardownCallback(callback, this.fmlViewDidLeaveDestructorCallbacks);
  }

  fmlViewWillEnter() {
    this.fmlViewWillEnterCallbacks.forEach((cb) => {
      const destructor = cb();
      if (cb.id) {
        this.fmlViewWillEnterDestructorCallbacks.push({ id: cb.id, destructor });
      }
    });
  }

  onFmlViewDidEnter(callback: LifeCycleCallback) {
    if (callback.id) {
      const index = this.fmlViewDidEnterCallbacks.findIndex((x) => x.id === callback.id);
      if (index > -1) {
        this.fmlViewDidEnterCallbacks[index] = callback;
      } else {
        this.fmlViewDidEnterCallbacks.push(callback);
      }
    } else {
      this.fmlViewDidEnterCallbacks.push(callback);
    }
  }

  fmlViewDidEnter() {
    this.fmlViewDidEnterCallbacks.forEach((cb) => {
      const destructor = cb();
      if (cb.id) {
        this.fmlViewDidEnterDestructorCallbacks.push({ id: cb.id, destructor });
      }
    });
  }

  onFmlViewWillLeave(callback: LifeCycleCallback) {
    if (callback.id) {
      const index = this.fmlViewWillLeaveCallbacks.findIndex((x) => x.id === callback.id);
      if (index > -1) {
        this.fmlViewWillLeaveCallbacks[index] = callback;
      } else {
        this.fmlViewWillLeaveCallbacks.push(callback);
      }
    } else {
      this.fmlViewWillLeaveCallbacks.push(callback);
    }
  }

  fmlViewWillLeave() {
    this.fmlViewWillLeaveCallbacks.forEach((cb) => {
      const destructor = cb();
      if (cb.id) {
        this.fmlViewWillLeaveDestructorCallbacks.push({ id: cb.id, destructor });
      }
    });
  }

  onFmlViewDidLeave(callback: LifeCycleCallback) {
    if (callback.id) {
      const index = this.fmlViewDidLeaveCallbacks.findIndex((x) => x.id === callback.id);
      if (index > -1) {
        this.fmlViewDidLeaveCallbacks[index] = callback;
      } else {
        this.fmlViewDidLeaveCallbacks.push(callback);
      }
    } else {
      this.fmlViewDidLeaveCallbacks.push(callback);
    }
  }

  fmlViewDidLeave() {
    this.fmlViewDidLeaveCallbacks.forEach((cb) => {
      const destructor = cb();
      if (cb.id) {
        this.fmlViewDidLeaveDestructorCallbacks.push({ id: cb.id, destructor });
      }
    });
    this.componentCanBeDestroyed();
  }

  onComponentCanBeDestroyed(callback: () => void) {
    this.componentCanBeDestroyedCallback = callback;
  }

  componentCanBeDestroyed() {
    if (this.componentCanBeDestroyedCallback) {
      this.componentCanBeDestroyedCallback();
    }
  }
};
